import React from "react";

import { createPortal } from "react-dom";

import "./DashboardRenovationCard.css";

import { useTranslation } from "react-i18next";

import { useNavigate } from "react-router-dom";

import { useAuth0 } from "@auth0/auth0-react";

import {

  AlertTriangle,

  FolderKanban,

  Loader2,

  ReceiptText,

  ShieldCheck,

  Unplug,

  X,

} from "lucide-react";

import { useToast } from "../../../components/feedback/ToastProvider";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

/* =========================================================*

*   FORMATTERS*

*   ========================================================= */

function parseAmount(value) {

  if (value === null || value === undefined || value === "") {

    return null;

  }

  const normalized =

    typeof value === "string" ? value.replace(/,/g, "").trim() : value;

  const numeric = Number(normalized);

  return Number.isFinite(numeric) ? numeric : null;

}

function formatEuro(value) {

  const numeric = parseAmount(value);

  if (numeric == null) {

    return "—";

  }

  return `€ ${numeric.toLocaleString("en-US", {

    minimumFractionDigits: 0,

    maximumFractionDigits: 2,

  })}`;

}

function formatDateTime(value) {

  if (!value) {

    return "—";

  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {

    return "—";

  }

  return date.toLocaleString("en-GB", {

    day: "2-digit",

    month: "short",

    year: "numeric",

    hour: "2-digit",

    minute: "2-digit",

  });

}

function firstDefined(...values) {

  for (const value of values) {

    if (value !== null && value !== undefined && value !== "") {

      return value;

    }

  }

  return null;

}

function isConfirmedFundingStatus(status) {

  return ["CONFIRMED", "APPROVED", "ACTIVE"].includes(

    String(status || "")

      .trim()

      .toUpperCase(),

  );

}

function getSupplementaryFundingSources(summary) {

  const sources = Array.isArray(summary?.funding_sources)

    ? summary.funding_sources

    : Array.isArray(summary?.loan_result_funding_sources)

      ? summary.loan_result_funding_sources

      : [];

  return sources.filter((source) => {

    const type = String(source?.source_type || "")

      .trim()

      .toUpperCase();

    return ["SUBSIDY", "GRANT", "SOFT_LOAN", "OTHER"].includes(type);

  });

}

/* =========================================================*

*   OSS STATUS*

*   ========================================================= */

function getOssStatusConfig(summary, t) {

  const status = summary?.oss_connection_status;

  const hasPreservedProject = Boolean(

    summary?.project_id ||

    summary?.renovation_project_id ||

    summary?.has_preserved_renovation_project,

  );

  if (!summary?.has_oss_connection && hasPreservedProject) {

    return {

      pill: t("renovation.ossStatus.notConnected", "Not connected"),

      title: t("renovation.preservedProjectTitle", "Renovation project"),

      description: t(

        "renovation.preservedProjectDescription",

        "Your renovation project, invoices and financing history are preserved. You can connect another One-Stop Shop whenever you are ready.",

      ),

      tone: "muted",

    };

  }

  if (!summary?.has_oss_connection) {

    return {

      pill: t("renovation.ossStatus.notConnected", "Not connected"),

      title: t("renovation.noOssTitle", "Renovation support"),

      description: t(

        "renovation.noOssDescription",

        "Connect with a One-Stop Shop to receive support for your renovation.",

      ),

      tone: "muted",

    };

  }

  const isPrePurchase = summary?.oss_request_type === "PRE_PURCHASE";

  const ossName =

    summary?.oss_name || t("renovation.selectedOss", "selected One-Stop Shop");

  if (!summary?.has_oss_connection) {

    return {

      pill: t("renovation.ossStatus.notConnected", "Not connected"),

      title: t("renovation.noOssTitle", "Renovation support"),

      description: t(

        "renovation.noOssDescription",

        "Connect with a One-Stop Shop to receive support for your renovation.",

      ),

      tone: "muted",

    };

  }

  if (status === "PENDING") {

    return {

      pill: isPrePurchase

        ? t(

            "renovation.ossStatus.pendingPrePurchase",

            "Pending One-Stop Shop review",

          )

        : t("renovation.ossStatus.pending", "Pending"),

      title: isPrePurchase

        ? t("renovation.pendingPrePurchaseTitle", "Property search support")

        : t("renovation.pendingOssTitle", "Renovation request pending"),

      description: isPrePurchase

        ? t(

            "renovation.pendingPrePurchaseDescription",

            "Your request has been sent to {{ossName}}. Once they accept it, they can help you review property options and prepare for a future renovation plan.",

            {

              ossName,

            },

          )

        : t(

            "renovation.pendingOssDescription",

            "{{ossName}} is reviewing your renovation support request.",

            {

              ossName,

            },

          ),

      tone: "pending",

    };

  }

  if (status === "ACCEPTED") {

    return {

      pill: isPrePurchase

        ? t("renovation.ossStatus.acceptedPrePurchase", "OSS connected")

        : t("renovation.ossStatus.accepted", "Connected"),

      title: isPrePurchase

        ? t("renovation.prePurchaseTitle", "Property search support")

        : t("renovation.title", "Renovation project"),

      description: isPrePurchase

        ? t(

            "renovation.acceptedPrePurchaseDescription",

            "{{ossName}} is now connected and can support you while you search for a suitable property.",

            {

              ossName,

            },

          )

        : t(

            "renovation.acceptedOssDescription",

            "{{ossName}} is now connected to your renovation project.",

            {

              ossName,

            },

          ),

      tone: "active",

    };

  }

  if (status === "DECLINED") {

    return {

      pill: t("renovation.ossStatus.declined", "Declined"),

      title: isPrePurchase

        ? t(

            "renovation.declinedPrePurchaseTitle",

            "One-Stop Shop request declined",

          )

        : t("renovation.declinedOssTitle", "Renovation request declined"),

      description: isPrePurchase

        ? t(

            "renovation.declinedPrePurchaseDescription",

            "This One-Stop Shop declined your property search support request. You can select another One-Stop Shop.",

          )

        : t(

            "renovation.declinedOssDescription",

            "This One-Stop Shop declined your renovation support request. You can select another One-Stop Shop.",

          ),

      tone: "warning",

    };

  }

  return {

    pill: status || t("renovation.ossStatus.unknown", "Unknown"),

    title: isPrePurchase

      ? t("renovation.prePurchaseTitle", "Property search support")

      : t("renovation.title", "Renovation project"),

    description: t(

      "renovation.genericOssDescription",

      "Your One-Stop Shop connection information is available below.",

    ),

    tone: "muted",

  };

}

/* =========================================================*

*   COMPONENT*

*   ========================================================= */

export default function DashboardRenovationCard({

  summary = {},

  onRefresh,

  onConnectOss,

}) {

  const { t } = useTranslation("homebuyer-dashboard");

  const navigate = useNavigate();

  const { getAccessTokenSilently } = useAuth0();

  const { showToast } = useToast();

  const config = getOssStatusConfig(summary, t);

  const hasActiveOss = Boolean(summary?.has_oss_connection);

  const hasProjectHistory = Boolean(

    summary?.project_id ||

    summary?.renovation_project_id ||

    summary?.has_preserved_renovation_project,

  );

  const [disconnectModalOpen, setDisconnectModalOpen] = React.useState(false);

  const [disconnectModalClosing, setDisconnectModalClosing] =

    React.useState(false);

  const [disconnecting, setDisconnecting] = React.useState(false);

  const disconnectCloseTimerRef = React.useRef(null);

  function openDisconnectModal() {

    if (disconnecting || !summary?.oss_connection_request_id) {

      return;

    }

    setDisconnectModalClosing(false);

    setDisconnectModalOpen(true);

  }

  function closeDisconnectModal() {

    if (disconnecting || disconnectModalClosing) {

      return;

    }

    setDisconnectModalClosing(true);

    disconnectCloseTimerRef.current = window.setTimeout(() => {

      setDisconnectModalOpen(false);

      setDisconnectModalClosing(false);

    }, 220);

  }

  function closeDisconnectModalAfterSuccess() {

    return new Promise((resolve) => {

      setDisconnectModalClosing(true);

      disconnectCloseTimerRef.current = window.setTimeout(() => {

        setDisconnectModalOpen(false);

        setDisconnectModalClosing(false);

        resolve();

      }, 220);

    });

  }

  React.useEffect(() => {

    return () => {

      if (disconnectCloseTimerRef.current) {

        window.clearTimeout(disconnectCloseTimerRef.current);

      }

    };

  }, []);

  React.useEffect(() => {

    if (!disconnectModalOpen) {

      return undefined;

    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    function handleKeyDown(event) {

      if (event.key === "Escape" && !disconnecting) {

        closeDisconnectModal();

      }

    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {

      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleKeyDown);

    };

  }, [disconnectModalOpen, disconnecting]);

  async function handleDisconnectOss() {

    const requestId = summary?.oss_connection_request_id;

    if (!requestId || disconnecting) {

      return;

    }

    try {

      setDisconnecting(true);

      const accessToken = await getAccessTokenSilently();

      const response = await fetch(

        `${API_BASE}` +

          `/api/homeowner/oss-connections/` +

          `${requestId}/disconnect/`,

        {

          method: "POST",

          headers: {

            Authorization: `Bearer ${accessToken}`,

            Accept: "application/json",

            "Content-Type": "application/json",

          },

        },

      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {

        throw new Error(

          payload.detail ||

            t(

              "renovation.disconnect.errorMessage",

              "The One-Stop Shop could not be disconnected.",

            ),

        );

      }

/**

*       * Let the exit animation finish BEFORE*

*       * refreshMe potentially removes/replaces*

*       * this card.*

*       */

      await closeDisconnectModalAfterSuccess();

      showToast({

        type: "success",

        title: t(

          "renovation.disconnect.successTitle",

          "One-Stop Shop disconnected",

        ),

        message:

          payload.message ||

          t(

            "renovation.disconnect.successMessage",

            "Your project, invoices and financing history have been preserved.",

          ),

      });

      await onRefresh?.();

    } catch (error) {

      console.error("Could not disconnect OSS:", error);

      showToast({

        type: "error",

        title: t(

          "renovation.disconnect.errorTitle",

          "Could not disconnect One-Stop Shop",

        ),

        message:

          error.message ||

          t(

            "renovation.disconnect.errorMessage",

            "The One-Stop Shop could not be disconnected.",

          ),

      });

    } finally {

      setDisconnecting(false);

    }

  }

/* =======================================================*

*     FLOW TYPE*

*     ======================================================= */

  const isPrePurchase = summary.oss_request_type === "PRE_PURCHASE";

  const isRenovation = !isPrePurchase;

/* =======================================================*

*     RENOVATION FINANCING*

*     ======================================================= */

  const hasApprovedLenderResult =

    summary?.loan_decision === "APPROVED" ||

    summary?.lender_result_status === "APPROVED" ||

    Boolean(summary?.has_agreed_lender_result);

  const renovationLoanAmount = hasApprovedLenderResult

    ? firstDefined(

        summary.approved_renovation_loan_amount,

        summary.agreed_renovation_loan_amount,

      )

    : summary.renovation_loan_amount;

  const lenderRenovationLoan = parseAmount(renovationLoanAmount) || 0;

  const renovationFinancingStatus = String(

    firstDefined(

      summary.renovation_financing_status,

      summary.loan_result_renovation_financing_status,

      "ESTIMATED",

    ),

  )

    .trim()

    .toUpperCase();

  const additionalRenovationLoans = Array.isArray(

    summary.additional_renovation_loans,

  )

    ? summary.additional_renovation_loans.filter((loan) => {

        const decision = String(loan?.decision || "APPROVED").toUpperCase();

        return decision === "APPROVED";

      })

    : [];

  const additionalLenderRenovationTotal = additionalRenovationLoans.reduce(

    (total, loan) =>

      total +

      (parseAmount(

        firstDefined(

          loan?.approved_renovation_loan_amount,

          loan?.approved_total_loan_amount,

          loan?.amount,

        ),

      ) || 0),

    0,

  );

  const confirmedAdditionalLenderRenovation = additionalRenovationLoans.reduce(

    (total, loan) => {

      const status = String(loan?.renovation_financing_status || "CONFIRMED")

        .trim()

        .toUpperCase();

      if (!isConfirmedFundingStatus(status)) {

        return total;

      }

      return (

        total +

        (parseAmount(

          firstDefined(

            loan?.approved_renovation_loan_amount,

            loan?.approved_total_loan_amount,

            loan?.amount,

          ),

        ) || 0)

      );

    },

    0,

  );

  const supplementaryFundingSources = getSupplementaryFundingSources(summary);

  const supplementaryFundingTotal = supplementaryFundingSources.reduce(

    (total, source) => total + (parseAmount(source?.amount) || 0),

    0,

  );

  const confirmedSupplementaryFunding = supplementaryFundingSources.reduce(

    (total, source) => {

      if (!isConfirmedFundingStatus(source?.status)) {

        return total;

      }

      return total + (parseAmount(source?.amount) || 0);

    },

    0,

  );

  const confirmedLenderRenovation =

    hasApprovedLenderResult && renovationFinancingStatus === "CONFIRMED"

      ? lenderRenovationLoan

      : 0;

  const potentialRenovationFunding = hasApprovedLenderResult

    ? lenderRenovationLoan +

      additionalLenderRenovationTotal +

      supplementaryFundingTotal

    : lenderRenovationLoan;

  const confirmedRenovationFunding = hasApprovedLenderResult

    ? confirmedLenderRenovation +

      confirmedAdditionalLenderRenovation +

      confirmedSupplementaryFunding

    : 0;

  const expectedRenovationFunding = Math.max(

    potentialRenovationFunding - confirmedRenovationFunding,

    0,

  );

  const renovationFinancingLabel = hasApprovedLenderResult

    ? t("renovation.potentialRenovationFunding", "Potential renovation funding")

    : t("renovation.requestedRenovationLoan", "Requested renovation financing");

/* =======================================================*

*     INVOICE / FUNDING VALUES*

*     ======================================================= */

  const invoicesIssued = parseAmount(summary.invoices_issued_amount) ?? 0;

  const totalFundingAllocated =

    parseAmount(summary.invoice_funding_allocated_amount) ?? 0;

  const invoiceUnallocated = Math.max(

    parseAmount(summary.invoice_unallocated_amount) ??

      invoicesIssued - totalFundingAllocated,

    0,

  );

  const potentialFundingRemaining = Math.max(

    potentialRenovationFunding - totalFundingAllocated,

    0,

  );

/* =======================================================*

*     INVOICE PROGRESS AGAINST TOTAL POTENTIAL FUNDING*

*     This is deliberately NOT allocation progress.*

*     It shows invoice expenditure against the full renovation*

*     funding envelope: lender loan + supplementary funding.*

*     ======================================================= */

  const invoiceProgress =

    potentialRenovationFunding > 0

      ? Math.min(

          Math.max((invoicesIssued / potentialRenovationFunding) * 100, 0),

          100,

        )

      : 0;

  const invoiceProgressLabel =

    potentialRenovationFunding > 0 ? `${invoiceProgress.toFixed(1)}%` : "0%";

  const allRenovationFundingConfirmed =

    hasApprovedLenderResult &&

    potentialRenovationFunding > 0 &&

    expectedRenovationFunding <= 0.009;

  const showFundingProgress =
    isRenovation && hasApprovedLenderResult &&
    (summary.oss_connection_status === "ACCEPTED" || hasProjectHistory);

  function handleChangeOss() {
    if (!summary?.oss_connection_request_id) {
      showToast({
        type: "error",
        title: t("renovation.changeOssUnavailableTitle", "Cannot change One-Stop Shop"),
        message: t("renovation.changeOssUnavailableMessage", "The current OSS request ID is missing. Refresh your dashboard and try again."),
      });
      return;
    }

    const property = summary?.oss_property_details ||
      summary?.property_information || summary?.project_property || null;
    const isPrePurchaseRequest = summary?.oss_request_type === "PRE_PURCHASE";
    const hasRenovationContext = Boolean(
      (property?.property_id || property?.id) &&
      (property?.market_region_id || property?.market_region?.id),
    );

    // Normally skip property editing entirely when replacing a partner.
    // For legacy dashboard responses, allow the property step to load
    // the dossier context before choosing an OSS.
    const destination = isPrePurchaseRequest
      ? "/hb/oss/partners/pre-purchase"
      : hasRenovationContext
        ? "/hb/oss/partners/renovation"
        : "/hb/oss/property-details";

    navigate(destination, {
      state: {
        changeOssMode: true,
        previousOssRequestId: summary.oss_connection_request_id,
        previousOssName: summary.oss_name,
        previousOssOrganisationId: summary.oss_organisation_id || null,
        dossierId: summary?.dossier_id || null,
        requestType: isPrePurchaseRequest ? "PRE_PURCHASE" : "RENOVATION",
        marketRegionId: property?.market_region_id ||
          property?.market_region?.id || null,
        marketRegion: property?.market_region || null,
        homebuyerCountry: property?.country || summary?.country_of_property_purchase || "",
        prePurchaseDetails: isPrePurchaseRequest
          ? {
              preferred_location: summary?.oss_preferred_location || "",
              property_type: summary?.oss_property_type_pref || "",
              timeline: summary?.oss_timeline || "",
              budget_range: summary?.oss_budget_range_text || "",
              notes: summary?.oss_request_notes || "",
            }
          : {},
        selectedMeasures: summary?.selected_measures || [],
        notes: summary?.oss_request_notes || "",
        ...(property && typeof property === "object"
          ? { propertyDetails: property }
          : {}),
      },
    });
  }

  function handleOpenProject() {

    const projectId = summary?.project_id || summary?.renovation_project_id;

    if (!projectId) {

      return;

    }

    navigate(`/hb/projects/${projectId}/property-information`);

  }

  return (

    <>

      <div

        className={`hb-renovation-card ` + `hb-renovation-card--${config.tone}`}

      >

        {/* ===================================================*

*          HEADER*

*          =================================================== */}

        <div className="hb-renovation-card__header">

          <div>

            <h3 className="hb-renovation-card__title">{config.title}</h3>

          </div>

          <span

            className={

              `hb-renovation-card__status ` +

              `hb-renovation-card__status--${config.tone}`

            }

          >

            {config.pill}

          </span>

        </div>

        {/* ===================================================*

*          DESCRIPTION*

*          =================================================== */}

        <p className="hb-renovation-card__description">{config.description}</p>

        {/* ===================================================*

*          MAIN STATS*

*          =================================================== */}

        <div className="hb-renovation-card__stats">

          {/* ---------------------------------------------------*

*            ONE-STOP SHOP*

*            --------------------------------------------------- */}

          <div className="hb-renovation-card__stat">

            <span className="hb-renovation-card__stat-label">

              {t("renovation.oss", "One-Stop Shop")}

            </span>

            <strong className="hb-renovation-card__stat-value">

              {hasActiveOss

                ? summary.oss_name || "—"

                : t("renovation.noActiveOss", "Not connected")}

            </strong>

            <small className="hb-renovation-card__stat-helper">

              {hasActiveOss

                ? isPrePurchase

                  ? t("renovation.searchPartner", "Property search partner")

                  : t(

                      "renovation.connectionPartner",

                      "Selected renovation partner",

                    )

                : t(

                    "renovation.noActiveOssHint",

                    "No active renovation partner",

                  )}

            </small>

          </div>

          {/* ---------------------------------------------------*

*            RENOVATION FINANCING / LOCATION*

*            --------------------------------------------------- */}

          <div className="hb-renovation-card__stat">

            <span className="hb-renovation-card__stat-label">

              {isPrePurchase

                ? t("renovation.preferredLocation", "Preferred location")

                : renovationFinancingLabel}

            </span>

            <strong className="hb-renovation-card__stat-value">

              {isPrePurchase

                ? summary.oss_preferred_location || "—"

                : formatEuro(potentialRenovationFunding)}

            </strong>

            <small className="hb-renovation-card__stat-helper">

              {isPrePurchase

                ? t("renovation.propertySearch", "Property search")

                : hasApprovedLenderResult

                  ? allRenovationFundingConfirmed

                    ? t(

                        "renovation.allFundingConfirmed",

                        "All recorded renovation funding is confirmed",

                      )

                    : t(

                        "renovation.confirmedAndExpectedFunding",

                        "Confirmed {{confirmed}} · Expected {{expected}}",

                        {

                          confirmed: formatEuro(confirmedRenovationFunding),

                          expected: formatEuro(expectedRenovationFunding),

                        },

                      )

                  : t("renovation.indicativeAmount", "Requested amount")}

            </small>

          </div>

          {/* ---------------------------------------------------*

*            STATUS / PROPERTY TYPE*

*            --------------------------------------------------- */}

          <div className="hb-renovation-card__stat">

            <span className="hb-renovation-card__stat-label">

              {isPrePurchase

                ? t("renovation.propertyType", "Property type")

                : t("renovation.connectionStatus", "Connection status")}

            </span>

            <strong className="hb-renovation-card__stat-value">

              {isPrePurchase

                ? summary.oss_property_type_pref

                  ? t(

                      `renovation.propertyTypes.${summary.oss_property_type_pref}`,

                      summary.oss_property_type_pref,

                    )

                  : "—"

                : config.pill}

            </strong>

            <small className="hb-renovation-card__stat-helper">

              {isPrePurchase

                ? t("renovation.searchPreference", "Search preference")

                : hasActiveOss

                  ? t(

                      "renovation.connectionStatusHint",

                      "Updated by the One-Stop Shop",

                    )

                  : t(

                      "renovation.connectionStatusDisconnectedHint",

                      "Project data is retained",

                    )}

            </small>

          </div>

          {/* ---------------------------------------------------*

*            REQUEST / TIMELINE*

*            --------------------------------------------------- */}

          <div className="hb-renovation-card__stat">

            <span className="hb-renovation-card__stat-label">

              {isPrePurchase

                ? t("renovation.timeline", "Timeline")

                : hasActiveOss

                  ? t("renovation.requestId", "Request ID")

                  : t("renovation.projectReference", "Project reference")}

            </span>

            <strong className="hb-renovation-card__stat-value">

              {isPrePurchase

                ? summary.oss_timeline

                  ? t(

                      `renovation.timelines.${summary.oss_timeline}`,

                      summary.oss_timeline,

                    )

                  : "—"

                : hasActiveOss

                  ? summary.oss_connection_request_id || "—"

                  : summary.project_code || summary.project_id || "—"}

            </strong>

            <small className="hb-renovation-card__stat-helper">

              {isPrePurchase

                ? t("renovation.expectedTiming", "Expected timing")

                : hasActiveOss

                  ? t("renovation.requestReference", "Request reference")

                  : t(

                      "renovation.preservedProjectReference",

                      "Preserved renovation record",

                    )}

            </small>

          </div>

        </div>

        {/* ===================================================*

*          RENOVATION EXPENDITURE PROGRESS*

*          =================================================== */}

        {showFundingProgress ? (

          <>

            <div className="hb-renovation-card__progress">

              <div className="hb-renovation-card__progress-header">

                <span>

                  {t("renovation.invoicesIssued", "Invoices issued")}{" "}

                  <strong>{formatEuro(invoicesIssued)}</strong>

                </span>

                <span>

                  {t(

                    "renovation.potentialRenovationFunding",

                    "Potential renovation funding",

                  )}{" "}

                  <strong>{formatEuro(potentialRenovationFunding)}</strong>

                </span>

              </div>

              <div className="hb-renovation-card__progress-track">

                <div

                  className="hb-renovation-card__progress-fill"

                  style={{

                    width: `${invoiceProgress}%`,

                  }}

                />

              </div>

              <div className="hb-renovation-card__progress-footer">

                <span>

                  {t(

                    "renovation.invoiceProgressHelp",

                    "Invoice value compared with total potential renovation funding",

                  )}

                </span>

                <strong>{invoiceProgressLabel}</strong>

              </div>

            </div>

            {/* ===============================================*

*              PROJECT FUNDING / INVOICE SUMMARY*

*              =============================================== */}

            <div className="hb-renovation-card__invoice-meta">

              <div className="hb-renovation-card__invoice-meta-item">

                <span>{t("renovation.invoicesIssued", "Invoices issued")}</span>

                <strong>{formatEuro(invoicesIssued)}</strong>

              </div>

              <div className="hb-renovation-card__invoice-meta-item">

                <span>

                  {t(

                    "renovation.invoiceFundingAllocated",

                    "Total funding allocated",

                  )}

                </span>

                <strong>{formatEuro(totalFundingAllocated)}</strong>

              </div>

              <div className="hb-renovation-card__invoice-meta-item">

                <span>

                  {t(

                    "renovation.invoiceUnallocatedAmount",

                    "Unallocated invoice amount",

                  )}

                </span>

                <strong>{formatEuro(invoiceUnallocated)}</strong>

              </div>

              <div className="hb-renovation-card__invoice-meta-item">

                <span>

                  {t(

                    "renovation.confirmedRenovationFunding",

                    "Confirmed renovation funding",

                  )}

                </span>

                <strong>{formatEuro(confirmedRenovationFunding)}</strong>

              </div>

              <div className="hb-renovation-card__invoice-meta-item">

                <span>

                  {t(

                    "renovation.potentialFundingRemaining",

                    "Potential funding remaining",

                  )}

                </span>

                <strong>{formatEuro(potentialFundingRemaining)}</strong>

              </div>

              <div className="hb-renovation-card__invoice-meta-item">

                <span>{t("renovation.lastUpdate", "Last update")}</span>

                <strong>

                  {formatDateTime(summary.renovation_last_update)}

                </strong>

              </div>

            </div>

          </>

        ) : null}

        {/* ===================================================*

*    ACTIONS*

*    =================================================== */}

        {hasActiveOss || hasProjectHistory ? (

          <div className="hb-renovation-card__actions">

            {/* OPEN PRESERVED / ACTIVE PROJECT */}

            {isRenovation && hasProjectHistory ? (

              <button

                type="button"

                className={

                  "hb-renovation-card__button " +

                  "hb-renovation-card__button--primary"

                }

                onClick={handleOpenProject}

              >

                {t("renovation.openProject", "Open renovation project")}

              </button>

            ) : null}

            {/* ACTIVE OSS */}

            {hasActiveOss ? (

              <>

                <button

                  type="button"

                  className={"hb-renovation-card__button"}

                  onClick={handleChangeOss}

                  disabled={disconnecting}

                >

                  {t("renovation.changeOss", "Change One-Stop Shop")}

                </button>

                <button

                  type="button"

                  className={

                    "hb-renovation-card__button " +

                    "hb-renovation-card__button--danger"

                  }

                  onClick={openDisconnectModal}

                  disabled={disconnecting}

                >

                  <Unplug size={14} />

                  {summary.oss_connection_status === "PENDING"

                    ? t(

                        "renovation.disconnect.cancelRequest",

                        "Cancel OSS request",

                      )

                    : t(

                        "renovation.disconnect.button",

                        "Disconnect One-Stop Shop",

                      )}

                </button>

              </>

            ) : (

/* NO OSS, PRESERVED PROJECT */

              <button

                type="button"

                className={"hb-renovation-card__button"}

                onClick={() => onConnectOss?.()}

              >

                {t("renovation.connectAnotherOss", "Connect a One-Stop Shop")}

              </button>

            )}

          </div>

        ) : null}

      </div>

      {disconnectModalOpen

        ? createPortal(

            <div

              className={

                "hb-oss-disconnect-modal__backdrop " +

                (disconnectModalClosing ? "is-closing" : "")

              }

              onMouseDown={(event) => {

                if (event.target === event.currentTarget && !disconnecting) {

                  closeDisconnectModal();

                }

              }}

            >

              <section

                className={

                  "hb-oss-disconnect-modal " +

                  (disconnectModalClosing ? "is-closing" : "")

                }

                role="dialog"

                aria-modal="true"

                aria-labelledby={"hb-oss-disconnect-title"}

                aria-describedby={"hb-oss-disconnect-description"}

              >

                <button

                  type="button"

                  className={"hb-oss-disconnect-modal__close"}

                  onClick={closeDisconnectModal}

                  disabled={disconnecting}

                  aria-label={t("common.close", "Close")}

                >

                  <X size={18} />

                </button>

                <div className={"hb-oss-disconnect-modal__icon"}>

                  <Unplug size={23} />

                </div>

                <div className={"hb-oss-disconnect-modal__heading"}>

                  <h2 id={"hb-oss-disconnect-title"}>

                    {summary.oss_connection_status === "PENDING"

                      ? t(

                          "renovation.disconnect.pendingTitle",

                          "Cancel One-Stop Shop request?",

                        )

                      : t(

                          "renovation.disconnect.title",

                          "Disconnect One-Stop Shop?",

                        )}

                  </h2>

                  <p id={"hb-oss-disconnect-description"}>

                    {summary.oss_connection_status === "PENDING"

                      ? t(

                          "renovation.disconnect.pendingDescription",

                          "Your current request to {{ossName}} will be cancelled.",

                          {

                            ossName:

                              summary.oss_name ||

                              t(

                                "renovation.selectedOss",

                                "the selected One-Stop Shop",

                              ),

                          },

                        )

                      : t(

                          "renovation.disconnect.description",

                          "{{ossName}} will no longer be connected to your renovation project.",

                          {

                            ossName:

                              summary.oss_name ||

                              t(

                                "renovation.selectedOss",

                                "The selected One-Stop Shop",

                              ),

                          },

                        )}

                  </p>

                </div>

                {summary.oss_name ? (

                  <div className={"hb-oss-disconnect-modal__organisation"}>

                    <span>{t("renovation.oss", "One-Stop Shop")}</span>

                    <strong>{summary.oss_name}</strong>

                  </div>

                ) : null}

                <div className={"hb-oss-disconnect-modal__preserved"}>

                  <div>

                    <span className={"hb-oss-disconnect-modal__preserved-icon"}>

                      <FolderKanban size={17} />

                    </span>

                    <div>

                      <strong>

                        {t(

                          "renovation.disconnect.projectPreservedTitle",

                          "Renovation project stays",

                        )}

                      </strong>

                      <p>

                        {t(

                          "renovation.disconnect.projectPreservedText",

                          "All existing renovation project information remains stored in LEG-UP.",

                        )}

                      </p>

                    </div>

                  </div>

                  <div>

                    <span className={"hb-oss-disconnect-modal__preserved-icon"}>

                      <ReceiptText size={17} />

                    </span>

                    <div>

                      <strong>

                        {t(

                          "renovation.disconnect.invoicesPreservedTitle",

                          "Invoices and financing stay",

                        )}

                      </strong>

                      <p>

                        {t(

                          "renovation.disconnect.invoicesPreservedText",

                          "Existing invoices, funding allocations and lender records are not removed.",

                        )}

                      </p>

                    </div>

                  </div>

                  <div>

                    <span className={"hb-oss-disconnect-modal__preserved-icon"}>

                      <ShieldCheck size={17} />

                    </span>

                    <div>

                      <strong>

                        {t(

                          "renovation.disconnect.historyPreservedTitle",

                          "Historical information is preserved",

                        )}

                      </strong>

                      <p>

                        {t(

                          "renovation.disconnect.historyPreservedText",

                          "Previous messages and activity remain as historical records, while the current OSS loses active access.",

                        )}

                      </p>

                    </div>

                  </div>

                </div>

                <div className={"hb-oss-disconnect-modal__notice"}>

                  <AlertTriangle size={16} />

                  <span>

                    {t(

                      "renovation.disconnect.notice",

                      "You can connect another One-Stop Shop later without deleting your existing renovation history.",

                    )}

                  </span>

                </div>

                <div className={"hb-oss-disconnect-modal__actions"}>

                  <button

                    type="button"

                    className={"hb-oss-disconnect-modal__cancel"}

                    onClick={closeDisconnectModal}

                    disabled={disconnecting}

                  >

                    {t("actions.cancel", "Cancel")}

                  </button>

                  <button

                    type="button"

                    className={"hb-oss-disconnect-modal__confirm"}

                    onClick={handleDisconnectOss}

                    disabled={disconnecting}

                  >

                    {disconnecting ? (

                      <>

                        <Loader2

                          size={16}

                          className={"hb-oss-disconnect-modal__spinner"}

                        />

                        {t(

                          "renovation.disconnect.disconnecting",

                          "Disconnecting...",

                        )}

                      </>

                    ) : (

                      <>

                        <Unplug size={16} />

                        {summary.oss_connection_status === "PENDING"

                          ? t(

                              "renovation.disconnect.confirmCancel",

                              "Cancel request",

                            )

                          : t("renovation.disconnect.confirm", "Disconnect")}

                      </>

                    )}

                  </button>

                </div>

              </section>

            </div>,

            document.body,

          )

        : null}

    </>

  );

}
